import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  RefreshCw, 
  Download,
  Users,
  Mail,
  Calendar,
  Trash2
} from 'lucide-react';
import apiService from '../../services/api';

const ManageContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  const ACCESS_PASSWORD = 'PASSword123$';

  useEffect(() => {
    const isAuth = sessionStorage.getItem('bandn_contracts_auth');
    if (isAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchContracts();
    }
  }, [isAuthenticated]);

  const handleAuth = (e) => {
    e.preventDefault();
    if (password === ACCESS_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      sessionStorage.setItem('bandn_contracts_auth', 'true');
    } else {
      setAuthError('Invalid password. Please try again.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('bandn_contracts_auth');
    setPassword('');
  };

  const fetchContracts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await apiService.getContracts();
      setContracts(data.data.contracts || []);
      setSelectedIds([]);
      setSelectAll(false);
    } catch (err) {
      setError(err.message || 'Failed to fetch contracts');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(contracts.map(c => c.contractId));
      setSelectAll(true);
    }
  };

  const handleSelectOne = (contractId) => {
    if (selectedIds.includes(contractId)) {
      const newSelected = selectedIds.filter(id => id !== contractId);
      setSelectedIds(newSelected);
      setSelectAll(false);
    } else {
      const newSelected = [...selectedIds, contractId];
      setSelectedIds(newSelected);
      if (newSelected.length === contracts.length) {
        setSelectAll(true);
      }
    }
  };

  const handleSelectTop100 = () => {
    const top100 = contracts.slice(0, 100).map(c => c.contractId);
    setSelectedIds(top100);
    setSelectAll(false);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('No contracts selected');
      return;
    }

    if (selectedIds.length > 100) {
      alert('Cannot delete more than 100 contracts at once. Please select up to 100 items.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} contract(s)? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const result = await apiService.deleteContracts(selectedIds);
      await fetchContracts();
      alert(`Successfully deleted ${result.data.deleted} contract(s)`);
    } catch (err) {
      console.error('Failed to delete contracts:', err);
      alert(`Failed to delete contracts: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleExportCSV = () => {
    if (contracts.length === 0) {
      alert('No contracts to export');
      return;
    }

    const headers = ['Date', 'Full Name', 'Email', 'Contract File'];
    
    const rows = contracts.map(contract => [
      formatDate(contract.submittedAt),
      contract.fullName,
      contract.email,
      contract.contractFileName
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bandn_contracts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-bnCream flex items-center justify-center px-6">
        <motion.div
          className="max-w-md w-full bg-white rounded-2xl border border-bnBorder p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-8">
            <h2 className="font-display font-light text-bnInk text-2xl mb-2">Admin Access Required</h2>
            <p className="text-bnBody">
              Please enter the password to access contract management.
            </p>
          </div>

          <form onSubmit={handleAuth}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-bnInk mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-bnBorder rounded-lg focus:ring-2 focus:ring-bnAccent focus:border-bnAccent"
                placeholder="Enter access password"
                required
              />
            </div>

            {authError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
                {authError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-bnAccent text-white py-3.5 px-4 rounded-full font-medium hover:bg-bnAccentDark transition-colors"
            >
              Access Contract Management
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-bnCream min-h-screen">
      <section className="bg-white border-b border-bnBorder py-12">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between"
          >
            <div>
              <p className="text-bnAccent text-xs font-medium tracking-[0.22em] uppercase mb-3">Admin</p>
              <h1 className="font-display font-light text-bnInk text-3xl mb-2">Contract Management</h1>
              <p className="text-bnBody">Review and manage submitted contracts</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleLogout}
                className="text-bnBody hover:text-bnAccent transition-colors text-sm"
              >
                Logout
              </button>
              <button
                onClick={fetchContracts}
                className="flex items-center gap-2 bg-bnAccent text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-bnAccentDark transition-colors"
              >
                <RefreshCw size={18} />
                Refresh
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="container mx-auto px-6 lg:px-12 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 border border-bnBorder mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-bnBody">Total Contracts</p>
              <p className="font-display font-light text-bnInk text-3xl mt-1">{contracts.length}</p>
            </div>
            <Users className="text-bnAccent" size={32} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-bnBorder p-6 mb-6"
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-normal text-bnInk text-xl">Submitted Contracts</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportCSV}
                  disabled={contracts.length === 0}
                  className="flex items-center gap-2 px-5 py-2.5 bg-bnInk text-white rounded-full hover:bg-bnInk/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <Download size={16} />
                  Export as CSV
                </button>
              </div>
            </div>

            {contracts.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-1.5 border border-bnBorder text-bnInk rounded-full hover:border-bnAccent hover:text-bnAccent transition-colors text-sm"
                >
                  {selectAll ? 'Deselect All' : 'Select All'}
                </button>
                {contracts.length > 100 && (
                  <button
                    onClick={handleSelectTop100}
                    className="px-4 py-1.5 border border-bnBorder text-bnInk rounded-full hover:border-bnAccent hover:text-bnAccent transition-colors text-sm"
                  >
                    Select Top 100
                  </button>
                )}
                {selectedIds.length > 0 && (
                  <>
                    <button
                      onClick={handleClearSelection}
                      className="px-4 py-1.5 border border-bnBorder text-bnInk rounded-full hover:border-bnAccent hover:text-bnAccent transition-colors text-sm"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      disabled={deleting}
                      className="flex items-center gap-2 px-5 py-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      <Trash2 size={14} />
                      {deleting ? 'Deleting...' : `Delete Selected (${selectedIds.length})`}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border border-bnBorder overflow-hidden"
        >
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="animate-spin mx-auto mb-4 text-bnAccent" size={32} />
              <p className="text-bnBody">Loading contracts...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <p className="text-red-600">{error}</p>
            </div>
          ) : contracts.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="mx-auto mb-4 text-bnAccent" size={32} />
              <p className="text-bnBody">No contracts submitted yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-bnCream border-b border-bnBorder">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-bnAccent border-bnBorder rounded focus:ring-bnAccent"
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Full Name
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-2 text-left text-[10px] font-medium text-bnBody uppercase tracking-wider">
                      Contract File
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-bnBorder">
                  {contracts.map((contract) => (
                    <tr key={contract.contractId} className="hover:bg-bnCream transition-colors">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(contract.contractId)}
                          onChange={() => handleSelectOne(contract.contractId)}
                          className="w-4 h-4 text-bnAccent border-bnBorder rounded focus:ring-bnAccent"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-bnInk flex items-center gap-2">
                          <Calendar size={14} className="text-bnAccent" />
                          {formatDate(contract.submittedAt)}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-bnInk">{contract.fullName}</div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-xs text-bnInk flex items-center gap-2">
                          <Mail size={14} className="text-bnAccent" />
                          {contract.email}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-xs text-bnInk flex items-center gap-2">
                          <FileText size={14} className="text-bnAccent" />
                          {contract.contractFileName}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </section>
    </div>
  );
};

export default ManageContracts;
